import { LuiMaterialTable } from '@jda/lui-common-component-library';
import { useLazyQuery } from '@apollo/react-hooks';
import React, { useEffect, useState, useRef } from 'react';
import { EVENT_REQUEST_RESPONSE_QUERY, ARCHIVE_REQUEST_RESPONSE_QUERY } from '../graphQL/queries';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Button,
  DialogActions,
  Select,
  MenuItem,
  Typography
} from '@material-ui/core';
import { VerticalLinearStepper } from './AuditDetails';
import EventTypeConfig from './config';
import { Loading } from '../../Loading';
import JsonEditor from '../../components/JsonEditor';
import { getColumns } from './columns';
import DocumentProcessor from './DocumentProcessor';
import { useSnackbar } from 'notistack';
import { formatString } from '../../utils/helpers';

interface ManageRelationsProps {
  dialogOpen: true | false;
  handleDialogClose: any;
  formData: any;
  mode: any;
  dataType?: string;
  participantCode?: any;
  handleDownloadClose?: any;
  handleDownload?: (FormData: any) => void;
  downloadJSONData?: any;
  setDownloadJSONData?: (value: any) => void;
}

const materialTableOptions = {
  headerStyle: {
    backgroundColor: '#EBEBEB',
    color: '#3F4756 '
  }
};

const EventDetailsDialog = (props: ManageRelationsProps) => {
  const { downloadJSONData, handleDownloadClose, participantCode } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [postTransformRequest, setPostTransformRequest] = useState({});
  const [preTransformResponse, setPreTransformResponse] = useState({});
  const [request, setRequest] = useState({});
  const [response, setResponse] = useState({});
  const [additionalInfo, setAdditionalInfo] = useState({});
  const [replayRetry, setReplayRetry] = useState([]);
  const [type, setType] = useState('RequestResponse');
  const [requestResponseData, setRequestResponseData] = useState<any>({});
  const [documentsData, setDocumentsData] = useState({});

  let [getEventDetails, { loading: eventRequestResponseLoading, data: eventRequestResponseData }] = useLazyQuery(
    EVENT_REQUEST_RESPONSE_QUERY,
    {
      variables: { eventId: props.formData.eventId }
    }
  );
  let [getArchiveDetals, { data: archiveRequestResponseData }] = useLazyQuery(ARCHIVE_REQUEST_RESPONSE_QUERY, {
    variables: { eventId: props.formData.eventId }
  });

  const jsonRequestEditorRef = useRef<any>();
  const jsonResponseEditorRef = useRef<any>();
  const jsonAdditionalInfoEditorRef = useRef<any>();
  const jsonPostTransformRequestEditorRef = useRef<any>();
  const jsonPreTransformResponseEditorRef = useRef<any>();

  const downloadJSONFile = ({ data, fileName, fileType }: any) => {
    const blob = new Blob([data], { type: fileType });
    const a = document.createElement('a');
    a.download = fileName;

    a.href = window.URL.createObjectURL(blob);
    a.click();
  };

  const exportToJson = (jsonData: any, name: any) => {
    downloadJSONFile({
      data: jsonData,
      fileName: name + '.json',
      fileType: 'text/json'
    });
  };

  useEffect(() => {
    if (props?.formData?.eventId) {
      if (props?.dataType === 'archive') {
        getArchiveDetals();
      } else {
        getEventDetails();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.formData?.eventId, props?.dataType]);

  const getFormattedData = (input: any) => {
    try {
      return formatString(input);
    } catch (e) {
      const res: any = { message: input };
      return res;
    }
  };

  useEffect(() => {
    if (props?.formData?.retryCount && props?.formData?.retryHistory?.length > 0) {
      setType('ReplayRetryHistory');
    } else {
      setType('RequestResponse');
    }
    if (downloadJSONData && eventRequestResponseData?.requestResponse?.requestBody) {
      exportToJson(
        eventRequestResponseData?.requestResponse?.requestBody,
        props?.formData?.uniqueCustomerTransactionId +
          '_' +
          props?.formData?.operation +
          '_' +
          participantCode +
          '_' +
          props?.formData?.apiDateTime +
          '_' +
          'input'
      );
      enqueueSnackbar('Request has been downloaded successfully !!', {
        variant: 'success',
        preventDuplicate: true
      });
      props.handleDownloadClose();
    }

    if (downloadJSONData && eventRequestResponseData?.requestResponse?.responseBody) {
      exportToJson(
        eventRequestResponseData?.requestResponse?.responseBody,
        props?.formData?.uniqueCustomerTransactionId +
          '_' +
          props?.formData?.operation +
          '_' +
          participantCode +
          '_' +
          props?.formData?.apiDateTime +
          '_' +
          'output'
      );
      enqueueSnackbar('Response has been downloaded successfully !!', {
        variant: 'success',
        preventDuplicate: true
      });
      props.handleDownloadClose();
    }

    const requestresponseData = props?.dataType === 'live' ? eventRequestResponseData : archiveRequestResponseData;
    setRequestResponseData(requestresponseData);
    if (requestresponseData?.requestResponse) {
      const leftSectionData = requestresponseData?.requestResponse?.requestBody || '';
      const rightSectionData = requestresponseData?.requestResponse?.responseBody || '';
      const bottomSectionData = requestresponseData?.requestResponse?.additionalInfo || '';
      setRequest(leftSectionData ? getFormattedData(leftSectionData) : {});
      setResponse(rightSectionData ? getFormattedData(rightSectionData) : {});
      setAdditionalInfo(bottomSectionData ? getFormattedData(bottomSectionData) : {});
    }

    if (props.formData?.retryCount) {
      const replayRetryData = props.formData?.retryHistory || [];
      setReplayRetry(replayRetryData);
    }
  }, [eventRequestResponseData, archiveRequestResponseData, downloadJSONData, props.formData]);

  useEffect(() => {
    if (jsonRequestEditorRef && jsonRequestEditorRef.current) {
      jsonRequestEditorRef.current.set(request);
      jsonRequestEditorRef.current.expandAll && jsonRequestEditorRef.current.expandAll();
    }
    if (jsonResponseEditorRef && jsonResponseEditorRef.current) {
      jsonResponseEditorRef.current.set(response);
      jsonResponseEditorRef.current.expandAll && jsonResponseEditorRef.current.expandAll();
    }
    if (jsonAdditionalInfoEditorRef && jsonAdditionalInfoEditorRef.current) {
      jsonAdditionalInfoEditorRef.current.set(additionalInfo);
      jsonAdditionalInfoEditorRef.current.expandAll && jsonAdditionalInfoEditorRef.current.expandAll();
    }
    if (jsonPostTransformRequestEditorRef && jsonPostTransformRequestEditorRef.current) {
      jsonPostTransformRequestEditorRef.current.set(postTransformRequest);
      jsonPostTransformRequestEditorRef.current.expandAll && jsonPostTransformRequestEditorRef.current.expandAll();
    }
    if (jsonPreTransformResponseEditorRef && jsonPreTransformResponseEditorRef.current) {
      jsonPreTransformResponseEditorRef.current.set(preTransformResponse);
      jsonPreTransformResponseEditorRef.current.expandAll && jsonPreTransformResponseEditorRef.current.expandAll();
    }
  }, [request, response, additionalInfo, postTransformRequest, preTransformResponse]);

  const setRequestEditorRef = (instance: any) => {
    if (instance) {
      jsonRequestEditorRef.current = instance.jsonEditor;
    } else {
      jsonRequestEditorRef.current = null;
    }
  };

  const setResponseEditorRef = (instance: any) => {
    if (instance) {
      jsonResponseEditorRef.current = instance.jsonEditor;
    } else {
      jsonResponseEditorRef.current = null;
    }
  };

  const setAdditionalInfoEditorRef = (instance: any) => {
    if (instance) {
      jsonAdditionalInfoEditorRef.current = instance.jsonEditor;
    } else {
      jsonAdditionalInfoEditorRef.current = null;
    }
  };

  const setPostTransformRequestEditorRef = (instance: any) => {
    if (instance) {
      jsonPostTransformRequestEditorRef.current = instance.jsonEditor;
    } else {
      jsonPostTransformRequestEditorRef.current = null;
    }
  };

  const setPreTransformResponseEditorRef = (instance: any) => {
    if (instance) {
      jsonPreTransformResponseEditorRef.current = instance.jsonEditor;
    } else {
      jsonPreTransformResponseEditorRef.current = null;
    }
  };

  const handleChangeType = (e: any) => {
    const selectType = e.target.value;
    let leftSectionData = '';
    let rightSectionData = '';
    let postTransformRequestData = '';
    let preTransformResponseData = '';
    let replayRetryData = [];
    let bottomSectionData = [];
    let documentData = [];
    setType(selectType);
    const requestresponseData = props?.dataType === 'live' ? eventRequestResponseData : archiveRequestResponseData;
    if (selectType === 'RequestResponse') {
      leftSectionData = requestresponseData?.requestResponse?.requestBody || '';
      rightSectionData = requestresponseData?.requestResponse?.responseBody || '';
      bottomSectionData = requestresponseData?.requestResponse?.additionalInfo || '';
    } else if (selectType === 'TransformedRequest') {
      leftSectionData = requestresponseData?.requestResponse?.requestBody || '';
      postTransformRequestData = requestresponseData?.requestResponse?.postTransformRequest || '';
    } else if (selectType === 'TransformedResponse') {
      preTransformResponseData = requestresponseData?.requestResponse?.preTransformResponse || '';
      rightSectionData = requestresponseData?.requestResponse?.responseBody || '';
    } else if (selectType === 'ReplayRetryHistory') {
      replayRetryData = props.formData?.retryHistory || [];
    } else if (selectType === 'Documents') {
      let formattedRequestData = requestresponseData?.requestResponse?.requestBody
        ? getFormattedData(requestresponseData?.requestResponse?.requestBody)
        : {};
      let formattedResponseData = requestresponseData?.requestResponse?.responseBody
        ? getFormattedData(requestresponseData?.requestResponse?.responseBody)
        : {};
      let documents: any[] = [];
      if (formattedResponseData?.bookingResponse?.documents) {
        documents = [...documents, ...formattedResponseData?.bookingResponse?.documents];
      }
      if (formattedResponseData?.documentContent) {
        documents.push(formattedResponseData);
      }
      if (formattedRequestData?.bookingResponse?.documents) {
        documents = [...documents, ...formattedRequestData?.bookingResponse?.documents];
      }
      if (formattedRequestData?.documentContent) {
        documents.push(formattedRequestData);
      }
      documentData = documents;
    }
    setRequest(leftSectionData ? getFormattedData(leftSectionData) : {});
    setResponse(rightSectionData ? getFormattedData(rightSectionData) : {});
    setAdditionalInfo(bottomSectionData ? getFormattedData(bottomSectionData) : {});
    setReplayRetry(replayRetryData);
    setDocumentsData(documentData);
    setPostTransformRequest(postTransformRequestData ? getFormattedData(postTransformRequestData) : {});
    setPreTransformResponse(preTransformResponseData ? getFormattedData(preTransformResponseData) : {});
  };

  return (
    <div>
      <Dialog
        onClose={props.handleDialogClose}
        open={props.dialogOpen}
        maxWidth={props?.dataType === 'archive' ? 'xl' : 'lg'}
        scroll="body"
        fullWidth
      >
        <DialogTitle id="customized-dialog-title">View Error Details</DialogTitle>
        <DialogContent dividers>
          {eventRequestResponseLoading ? (
            <Loading />
          ) : (
            <div>
              {props?.dataType === 'archive' ? (
                <>
                  <VerticalLinearStepper data={archiveRequestResponseData}>
                    <Grid container>
                      <Grid item xs={4}>
                        <Typography variant="button">Api Date Time: </Typography>
                        {requestResponseData?.requestResponse?.apiDateTime}
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="button">Error Message: </Typography>
                        {requestResponseData?.requestResponse?.errorMessage}
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="button">Event Type: </Typography>
                        {requestResponseData?.requestResponse?.eventType}
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item xs={4}>
                        <Typography variant="button">Message Id: </Typography>
                        {requestResponseData?.requestResponse?.messageId}
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="button">Operation Http Status: </Typography>
                        {requestResponseData?.requestResponse?.operationHttpStatus}
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="button">Request Type: </Typography>
                        {requestResponseData?.requestResponse?.requestType}
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item xs={4}>
                        <Typography variant="button">Response Status: </Typography>
                        {requestResponseData?.requestResponse?.responseStatus}
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="button">Status Code: </Typography>
                        {requestResponseData?.requestResponse?.statusCode}
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="button">Time Taken: </Typography>
                        {requestResponseData?.requestResponse?.timeTaken}
                      </Grid>
                    </Grid>
                  </VerticalLinearStepper>
                </>
              ) : (
                <>
                  <Select
                    style={{ width: '30%', marginTop: '10px', marginLeft: '20px' }}
                    data-id="event-type-id"
                    id="event-type-id"
                    value={type}
                    onChange={e => handleChangeType(e)}
                  >
                    <MenuItem value={EventTypeConfig.RequestResponse.value}>
                      {EventTypeConfig.RequestResponse.label}
                    </MenuItem>
                    <MenuItem value={EventTypeConfig.ReplayRetryHistory.value}>
                      {EventTypeConfig.ReplayRetryHistory.label}
                    </MenuItem>
                    {(props?.formData?.operation === 'booking_create' ||
                      props?.formData?.operation === 'documentRequest') && (
                      <MenuItem value={EventTypeConfig.Documents.value}>{EventTypeConfig.Documents.label}</MenuItem>
                    )}
                    {eventRequestResponseData?.requestResponse?.postTransformRequest ? (
                      <MenuItem value={EventTypeConfig.TransformedRequest.value}>
                        {EventTypeConfig.TransformedRequest.label}
                      </MenuItem>
                    ) : (
                      ''
                    )}
                    {eventRequestResponseData?.requestResponse?.preTransformResponse ? (
                      <MenuItem value={EventTypeConfig.TransformedResponse.value}>
                        {EventTypeConfig.TransformedResponse.label}
                      </MenuItem>
                    ) : (
                      ''
                    )}
                  </Select>
                  <Grid container style={{ width: '60vw' }}>
                    {type === 'RequestResponse' && (
                      <>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="button">{EventTypeConfig[type].leftHeader}</Typography>
                            <JsonEditor
                              setRef={setRequestEditorRef}
                              id={`jsonRequestEditor-${props?.formData?.eventId}`}
                              data={request}
                              mode={props.mode}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="button">{EventTypeConfig[type].rightHeader}</Typography>
                            <JsonEditor
                              setRef={setResponseEditorRef}
                              id={`jsonResponseEditor-${props?.formData?.eventId}`}
                              data={response}
                              mode={props.mode}
                            />
                          </Grid>
                          {/* <Grid item xs={4}>
                        <Typography variant="button">{EventTypeConfig[type].bottomHeader}</Typography>
                        <JsonEditor
                          setRef={setAdditionalInfoEditorRef}
                          id={`jsonResponseEditor-${props?.formData?.eventId}`}
                          data={additionalInfo}
                          mode={props.mode}
                        />
                      </Grid> */}
                        </Grid>
                      </>
                    )}
                    {type === 'TransformedRequest' && (
                      <>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="button">{EventTypeConfig[type].leftHeader}</Typography>
                            <JsonEditor
                              setRef={setRequestEditorRef}
                              id={`jsonRequestEditor-${props?.formData?.eventId}`}
                              data={request}
                              mode={props.mode}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="button">{EventTypeConfig[type].rightHeader}</Typography>
                            <JsonEditor
                              setRef={setPostTransformRequestEditorRef}
                              id={`jsonResponseEditor-${props?.formData?.eventId}`}
                              data={postTransformRequest}
                              mode={props.mode}
                            />
                          </Grid>
                          {/* <Grid item xs={4}>
                        <Typography variant="button">{EventTypeConfig[type].bottomHeader}</Typography>
                        <JsonEditor
                          setRef={setAdditionalInfoEditorRef}
                          id={`jsonResponseEditor-${props?.formData?.eventId}`}
                          data={additionalInfo}
                          mode={props.mode}
                        />
                      </Grid> */}
                        </Grid>
                      </>
                    )}
                    {type === 'TransformedResponse' && (
                      <>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="button">{EventTypeConfig[type].leftHeader}</Typography>
                            <JsonEditor
                              setRef={setResponseEditorRef}
                              id={`jsonRequestEditor-${props?.formData?.eventId}`}
                              data={response}
                              mode={props.mode}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="button">{EventTypeConfig[type].rightHeader}</Typography>
                            <JsonEditor
                              setRef={setPreTransformResponseEditorRef}
                              id={`jsonResponseEditor-${props?.formData?.eventId}`}
                              data={preTransformResponse}
                              mode={props.mode}
                            />
                          </Grid>
                          {/* <Grid item xs={4}>
                        <Typography variant="button">{EventTypeConfig[type].bottomHeader}</Typography>
                        <JsonEditor
                          setRef={setAdditionalInfoEditorRef}
                          id={`jsonResponseEditor-${props?.formData?.eventId}`}
                          data={additionalInfo}
                          mode={props.mode}
                        />
                      </Grid> */}
                        </Grid>
                      </>
                    )}
                    {type === 'ReplayRetryHistory' && (
                      <Grid item xs={5} style={{ margin: '10px' }}>
                        <Typography variant="button">
                          {`${EventTypeConfig[type].leftHeader} (${props.formData?.retryStatus})`}
                        </Typography>
                        <LuiMaterialTable
                          data={replayRetry}
                          columns={getColumns('')?.REPLAYRETRY}
                          materialTableProps={{
                            options: materialTableOptions
                          }}
                        />
                      </Grid>
                    )}
                    {type === 'Documents' && (
                      <Grid item xs={12} style={{ margin: '25px' }}>
                        {/* <Typography variant="button">
                      {`${EventTypeConfig[type].leftHeader} (${props.formData?.retryStatus})`}
                    </Typography> */}
                        <DocumentProcessor documentsData={documentsData} />
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={props.handleDialogClose} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default EventDetailsDialog;
